import styled, { css } from 'styled-components';
import { stylePerBreakpoint } from '../../helpers/mediaQuery';

export type GridProps = {
  alignLeft?: boolean;
};

/**
 * How is the fluid grid implemented?
 *
 * The fluid grid is defined as n columns, with (n-1) gutter widths.
 *
 * Grid:
 * Has a negative half a gutter on each side.
 * ----------------------------------------------
 * | | 1/2  |<- max-width - 1 gutter ->| 1/2  | |
 * | |gutter|                          |gutter| |
 * | |      |                          |      | |
 * | |      |                          |      | |
 * ----------------------------------------------
 *
 * GridItem:
 * Sits inside the Grid with a positive half a gutter on each side.
 * ----Grid------------Grid------------Grid------------Grid------------Grid------------Grid------------Grid-----
 * | 1/2  |        column         |  gutter  |        column         |  gutter  |        column         | 1/2  |
 * |gutter|        column         |  gutter  |        column         |  gutter  |        column         |gutter|
 * |      |[---------------- Grid item (2 columns) -----------------]|  gutter  |        column         |      |
 * |      |        column         |  gutter  |        column         |  gutter  |        column         |      |
 *  ------------------------------------------------------------------------------------------------------------
 *
 * The positive and negative gutters of Grid and GridItem cancel out,
 * leaving the items with a full gutter between items and no gutter at the edge.
 *
 * For a grid with n columns, we can calcuate the width of an item with m columns by using the proportion of m/n
 * (since there is an equal number of columns and gutters in the grid) and then subtracting the extra gutter width
 * (the halves on each side).
 *
 * Algebraically:
 * (c = column width, g = gutter width)
 *
 * What we want to calculate is: mc + (m-1)g (the width of the grid item).
 *
 * mc + (m-1)g
 *             = mc + mg - g
 *             = m/n(nc + ng) - g
 *             = m/n(grid width) - g
 */

/**
 * A container for the fluid grid. GridItems placed inside will conform to the grid specified
 * in the theme.
 *
 * @param alignLeft - if passed to the component, the child GridItems will be left-aligned.  The default state
 * is centre-aligned.
 */
export const Grid = styled.div<GridProps>`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: ${({ alignLeft }) => (alignLeft ? 'start' : 'center')};

  ${stylePerBreakpoint((breakpoint) => ({ theme }) => css`
    margin: 0 -${theme.layout[breakpoint].gutterPx / 2}px;
    width: calc(100% + ${theme.layout[breakpoint].gutterPx}px);
  `)}
`;
